import * as React from 'react';
import { CheckoutContext, Steps } from './utils/CheckoutContext';
import { CheckoutVenueRecommendationsCardsList, CheckoutVenueRecommendationsCardInterface } from '@weddingspot/ws-core-components';
import RFPForm from './RFPForm';
import { ItemsList } from './ItemsList/ItemsList';
import SuccessAlert from './MoreVenues/SuccessAlert';
import { CSNVenueType } from '@weddingspot/ws-api-client';
import { AnalyticsEvent, logEvent } from '@weddingspot/ws-analytics';

const styles = require('./CheckOutPage.module.scss');

enum HotelsRecommendationsSteps {
    ChooseStep = 1,
    FormStep = 2,
}

const HotelsRecommendations = () => {
    const [localStep, setLocalStep] = React.useState<HotelsRecommendationsSteps>(HotelsRecommendationsSteps.ChooseStep);

    const {
        step: globalStep,
        setStep,
        setConfirmButtonName,
        setSecondaryButtonName,
        setConfirmButtonAction,
        setSecondaryButtonAction,
        hotelRecommendationCards,
        setHotelRecommendationCards,
        hotelRecommendations,
        setIsRequested,
        RFPFormElement,
        moreVenuesSanded,
    } = React.useContext(CheckoutContext);

    const changeIsChecked = React.useCallback(
        (id: number) => {
            setHotelRecommendationCards(
                hotelRecommendationCards.map((item: CheckoutVenueRecommendationsCardInterface) => {
                    if (item.id === id) {
                        item.isChecked = !item.isChecked;
                    }
                    return item;
                })
            );
        },
        [hotelRecommendationCards]
    );

    const boutiqueRecommendationCards = hotelRecommendationCards.filter((i) => i.type === CSNVenueType.BOUTIQUE_HOTEL);
    const luxuryRecommendationCards = hotelRecommendationCards.filter((i) => i.type === CSNVenueType.LUXURY_HOTEL);
    const resortRecommendationCards = hotelRecommendationCards.filter((i) => i.type === CSNVenueType.RESORT);
    const otherRecommendationCards = hotelRecommendationCards.filter(
        // @ts-ignore
        (i) => i.type && [CSNVenueType.BLANK, CSNVenueType.HOTEL].includes(i.type)
    );

    const onSubmitRFP = () => {
        logEvent(AnalyticsEvent.EVTNM_SUBMITTED_HOTEL_RFP_FORM, {
            Source: 'Checkout page',
        });
        getFinalStep();
    };

    const getFinalStep = () => {
        setIsRequested(false);
        setStep(Steps.Final);
    };

    React.useEffect(() => {
        const submittedCards = hotelRecommendationCards.filter((item: CheckoutVenueRecommendationsCardInterface) => item.isChecked);
        if (globalStep === Steps.HotelsRecommendations) {
            setConfirmButtonName(`Contact ${submittedCards.length} hotels`);
            setSecondaryButtonName('Skip');
            setSecondaryButtonAction(getFinalStep);
            if (localStep === HotelsRecommendationsSteps.ChooseStep) {
                setConfirmButtonAction(() => setLocalStep(HotelsRecommendationsSteps.FormStep));
            }
            if (localStep === HotelsRecommendationsSteps.FormStep) {
                logEvent(AnalyticsEvent.EVTNM_SHOWN_HOTEL_ROOM_RFP_FORM, {
                    Source: 'checkout page flow',
                });
                setSecondaryButtonName('Cancel');
                setConfirmButtonAction(() => {
                    RFPFormElement?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                });
            }
        }
    }, [globalStep, localStep, hotelRecommendationCards, RFPFormElement]);

    React.useEffect(() => {
        logEvent(AnalyticsEvent.EVTNM_SHOWN_HOTEL_ROOM_RECOMMENDATIONS, {
            Source: 'Checkout page',
        });
    }, []);

    return (
        <>
            {localStep === HotelsRecommendationsSteps.ChooseStep ? (
                <div className={styles.hotelsRecommendations}>
                    {moreVenuesSanded && <SuccessAlert />}
                    <h2>Need hotel rooms for your guests ?</h2>
                    <div className={styles.hotelsRecommendationsFormTitleBody}>
                        <div>Hotel room blocks are a free and easy way to get the best rates out there. We can help you:</div>
                        <div>
                            <ul>
                                <li>Receive custom quotes</li>
                                <li>Access discounted rates</li>
                                <li>And ensure that you get the best possible price for your guest's hotel rooms</li>
                            </ul>
                        </div>
                        <div>Hotels we think you’ll love:</div>
                    </div>
                    {!!boutiqueRecommendationCards.length && (
                        <div className={styles.hotelsRecommendationsCardBlock}>
                            <h2>Boutique</h2>
                            <CheckoutVenueRecommendationsCardsList list={boutiqueRecommendationCards} changeIsChecked={changeIsChecked} />
                        </div>
                    )}
                    {!!luxuryRecommendationCards.length && (
                        <div className={styles.hotelsRecommendationsCardBlock}>
                            <h2>Luxury</h2>
                            <CheckoutVenueRecommendationsCardsList list={luxuryRecommendationCards} changeIsChecked={changeIsChecked} />
                        </div>
                    )}
                    {!!resortRecommendationCards.length && (
                        <div className={styles.hotelsRecommendationsCardBlock}>
                            <h2>Resort</h2>
                            <CheckoutVenueRecommendationsCardsList list={resortRecommendationCards} changeIsChecked={changeIsChecked} />
                        </div>
                    )}
                    {!!hotelRecommendationCards.length && (
                        <div className={styles.hotelsRecommendationsCardBlock}>
                            <h2>Hotel</h2>
                            <CheckoutVenueRecommendationsCardsList list={hotelRecommendationCards} changeIsChecked={changeIsChecked} />
                        </div>
                    )}
                </div>
            ) : (
                <div className={styles.hotelsRecommendationsForm}>
                    <div className={styles.hotelsRecommendationsFormTitleBlock}>
                        <div className={styles.hotelsRecommendationsFormTitle}>Reserve Hotel Rooms</div>
                        <ItemsList
                            items={hotelRecommendations.filter((i) => hotelRecommendationCards.find((j) => j.id === i.id)?.isChecked)}
                            name='hotels'
                            removeItem={(id: number) => () => changeIsChecked(id)}
                        />
                    </div>
                    <RFPForm hotelIds={hotelRecommendationCards.map((i) => i.id as string)} onSuccess={onSubmitRFP} />
                </div>
            )}
        </>
    );
};

export default HotelsRecommendations;
